import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { MONTHS, TRAVEL_CATEGORIES } from "../../lib/site";

const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const PHONE_RX = /^\+?[0-9][0-9\s-]{8,15}$/;

function validate(f) {
  const e = {};
  if (!f.name.trim() || f.name.trim().length < 2) e.name = "Please enter your full name";
  if (!PHONE_RX.test(f.phone.trim()) || f.phone.replace(/\D/g, "").length < 10) e.phone = "Enter a valid phone number";
  if (!EMAIL_RX.test(f.email.trim())) e.email = "Enter a valid email address";
  if (!f.travelers || Number(f.travelers) < 1) e.travelers = "At least 1 traveller";
  return e;
}

const Field = ({ label, error, children, id }) => (
  <div>
    <label htmlFor={id} className="label">{label}</label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

export const InquiryForm = ({ variant = "callback", tour, defaults = {}, idPrefix = "inquiry" }) => {
  const isContact = variant === "contact";
  const [f, setF] = useState({ name: "", phone: "", email: "", destination: tour?.destination || defaults.destination || "", travel_month: defaults.travel_month || "",
    travel_date: "", travelers: defaults.travelers || 2, category: defaults.category || "", message: "", website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const id = (k) => `${idPrefix}-${k}`;

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    setErrors(v);
    if (Object.keys(v).length || status === "loading") return;
    setStatus("loading");
    setServerError("");
    try {
      await api.post("/inquiries", { ...f, travelers: Number(f.travelers), tour_slug: tour?.slug || "", tour_title: tour?.title || "", source: isContact ? "contact" : tour ? "tour" : "callback" });
      setStatus("done");
    } catch (err) {
      setServerError(errMsg(err));
      setStatus("idle");
    }
  };

  if (status === "done") {
    return (
      <div className="rounded-2xl bg-[#FFF3EC] p-6 text-center" data-testid={`${idPrefix}-success`}>
        <CheckCircle2 className="mx-auto h-10 w-10 text-[#FD691B]" />
        <h3 className="mt-3 font-display text-lg font-bold">Thank you, we've received your enquiry!</h3>
        <p className="mt-2 text-sm text-slate-600">A Wandervibe travel expert will get in touch with you shortly. Please note this is an enquiry — your booking is not confirmed yet.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid={`${idPrefix}-form`}>
      <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <Field label="Full Name *" error={errors.name} id={id("name")}>
        <input id={id("name")} className="field" value={f.name} onChange={set("name")} placeholder="Your full name" data-testid={id("name")} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Phone Number *" error={errors.phone} id={id("phone")}>
          <input id={id("phone")} type="tel" className="field" value={f.phone} onChange={set("phone")} placeholder="+91 98xxxxxxxx" data-testid={id("phone")} />
        </Field>
        <Field label="Email Address *" error={errors.email} id={id("email")}>
          <input id={id("email")} type="email" className="field" value={f.email} onChange={set("email")} placeholder="you@email.com" data-testid={id("email")} />
        </Field>
      </div>
      {isContact && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Destination" id={id("destination")}>
            <input id={id("destination")} className="field" value={f.destination} onChange={set("destination")} placeholder="e.g. Spiti Valley" data-testid={id("destination")} />
          </Field>
          <Field label="Preferred Travel Month" id={id("month")}>
            <select id={id("month")} className="field" value={f.travel_month} onChange={set("travel_month")} data-testid={id("month")}>
              <option value="">Select month</option>
              {MONTHS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {isContact ? (
          <Field label="Travel Category" id={id("category")}>
            <select id={id("category")} className="field" value={f.category} onChange={set("category")} data-testid={id("category")}>
              <option value="">Select category</option>
              {TRAVEL_CATEGORIES.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
        ) : (
          <Field label="Preferred Travel Date" id={id("date")}>
            <input id={id("date")} type="date" className="field" value={f.travel_date} onChange={set("travel_date")} data-testid={id("date")} />
          </Field>
        )}
        <Field label="Number of Travelers *" error={errors.travelers} id={id("travelers")}>
          <input id={id("travelers")} type="number" min="1" max="100" className="field" value={f.travelers} onChange={set("travelers")} data-testid={id("travelers")} />
        </Field>
      </div>
      <Field label="Message" id={id("message")}>
        <textarea id={id("message")} rows={3} className="field resize-none" value={f.message} onChange={set("message")} placeholder="Tell us about your plans" data-testid={id("message")} />
      </Field>
      {serverError && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid={`${idPrefix}-server-error`}>{serverError}</p>}
      <button type="submit" disabled={status === "loading"} className="btn-brand w-full !py-3.5 disabled:opacity-70" data-testid={`${idPrefix}-submit-btn`}>
        {status === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
        {isContact ? "Plan My Trip" : "Request a Callback"}
      </button>
      <p className="text-center text-xs text-slate-500">We'll call you back. Submitting this form does not confirm a booking.</p>
    </form>
  );
};
